import constants from "../../../../appConstants";
import {
  formatDateTime,
  formatDate,
  suppressEnter,
  mergeUserConfig,
  formatDeliveryWindow,
  isValidTimeValue,
} from "../../../dataGrid/gridUtilities";
import { MultiSelectFilter, DateTimeFilter, StaticFilter } from "../../../dataGrid/columnFilters";
import { DropdownEditor, DateTimeEditor,NumericEditor, TextFieldEditor, DateTimeRangeEditor } from "../../../dataGrid/cellEditors";
import iconRenderer from "../../../common/iconRenderer"
import { DropdownValueCellRenderer, IBETARenderer, SpecialNeedsRenderer, SpecialNeedsValueGetter,GeoCodeRenderer, DotRenderer } from "./../../../dataGrid/cellRenderer/index";
import { COLUMN_CONFIG, REVERT_COLUMN_CONFIG } from "./constant";
import { getFormattedDeliveryDateTime } from "../../utils";
import RevertRenderer from "./RevertRenderer";

export default class GridConfig {
    constructor(configuration){
        const config = configuration || {};
        this.name = "GridConfiguration";
        this.config = GridConfig.getConfiguration(config);
    }

      static getConfiguration({
        userConfig,
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
        columnConfig,
      }) {
      const defaultConfig = GridConfig.getDefaultConfiguration({
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
          columnConfig
      });
      return mergeUserConfig({
        defaultConfig,
          userConfig,
      });
    }

    static isRouteNameEditable(params){
      return params.data.isInPlan;
    }

    static getProNumberClasses(params){
      return  ["gridCell-leftAlign", params.context.hasProImagesURL ?  "clickable-cell"  :""];
    }

    static getSpecialNeedsOption(params) {
      return params.context.specialNeedsOptions;
    }

    static IBETAEditable(params){
      if(params.data.actualArrivalTime){
        return false;
      }
      return true;
    }

    static shipmentTypeValueGetter(params){
      return params.data.isDelivery ? "D": "P";
    }

    static getAPTWindowConfig(columnConfig) {
      if (columnConfig === COLUMN_CONFIG.SAIA) {
        return {
          headerName: "Delivery Window",
          headerTooltip: "Delivery Window",
          valueGetter: (params) =>  params.data.shipmentCTA || params.data.deliveryWindow.join(","),
          valueFormatter: formatDeliveryWindow
        }
      }
      return {
        headerName: "APT Window",
        headerTooltip: "Appointment Window",
        valueGetter: (params) => params.data.shipmentCTA,
        valueFormatter: (params) => {
          if (!isValidTimeValue(params.value)) {
            return;
          }
          return params.value
        },
      };
    }

    static getCustomerName(params) {
      return params.data.isDelivery
        ? params.data.consigneeName
        : params.data.shipperName;
    }

    static getCustomerAddress(params) {
      return params.data.isDelivery
        ? params.data.consigneeAddress
        : params.data.shipperAddress;
    }

    static getCustomerCity(params) {
      return params.data.isDelivery
        ? params.data.city
        : params.data.shipperCity;
    }

    static getCustomerState(params) {
      return params.data.isDelivery
        ? params.data.state
        : params.data.shipperState;
    }

    static getCustomerZip(params) {
      return params.data.isDelivery
        ? params.data.zip
        : params.data.shipperZip;
    }

    static getFlexibleAppointment(params) {      
      if (params.data.isFlexiApt ) {
        return "Y";
      }
      return "";
    }

    static getCustomerCode(params) {
      return params.data.isDelivery
        ? params.data.consigneeCode
        : params.data.shipperCode;
    }

    static geoCodeValueGetter(params) {  
      return params.data.isDelivery ? params.data.isGeoCoded: params.data.isShipperGeoCoded;
    }
  
    static setFlexibleAppointment(params) {
      if (params.data.isFlexiApt === params.newValue.value) {
        return false;
      }
      params.data.isFlexiApt = params.newValue.value;
      return true;
    }

    static removeYear(datetime) {
      const [date, time] = datetime.split(' ');
      const [year, month, day] = date.split('-');
      return `${month}/${day} ${time}`;
    }
    static valueFormatterFFEAPT  (params){
      const {intervalSeperator} =  constants.DATETYPES.YEARDATETIME;
      if(params.value){
        const [startDateString, endDateString] = params.value.split(intervalSeperator)
        return `${GridConfig.removeYear(startDateString)} - ${GridConfig.removeYear(endDateString)}`
      }
      return null;
    }

    static aptWindowModifiedValueGetter(params) {   
      return params.data.isAptWindowModified ? "Y": "";   
    }

    static shipmentAtDestTerminalValueGetter(params) {   
      return params.data.isShipmentAtDestTerminal ? "Y": null;
    }

    static FFEAPTWindowRenderer(params) {
      return (
        <div className="gridCell-centerAlign">
          {params.valueFormatted || <div style={{ width: 142, opacity: 0.7 }}></div>}
          <RevertRenderer
            originalValue={GridConfig.valueFormatterFFEAPT({
              value: params.data.originalShipmentCTA
            })}
            gridParams={params}
          />
        </div>
      );
    }

    static FrozenAndNonFrozenPalletRenderer(params) {
      const fieldConfig = REVERT_COLUMN_CONFIG[params.colDef.field];
      return (
        <div className="gridCell-centerAlign">
          {params.value}
          <RevertRenderer
            originalValue={params.data[fieldConfig.originalValueField]}
            gridParams={params}
          />
        </div>
      );
    }
   

    static getAPTWindowColumnConfig(aptConfig, featureFlags) {
      if (featureFlags.isFFEConfig) {
        return [
          {
            headerName: aptConfig.headerName,
            headerTooltip: aptConfig.headerTooltip,
            field: "shipmentCTA",
            minWidth: constants.columnWidth.APT_WINDOW,
            cellClass: "gridCell-leftAlign gridCell-centerAlign",
            filterFramework: DateTimeFilter,
            filterParams: {
              DATETYPE: constants.DATETYPES.DATETIME,
              isTimeInterval: true,
            },
            editable: true,
            valueFormatter: GridConfig.valueFormatterFFEAPT,
            cellRendererFramework:GridConfig.FFEAPTWindowRenderer,
            cellEditorFramework: DateTimeRangeEditor,
            suppressKeyboardEvent: (params) => params.editing,
            cellEditorParams: {
              DATETYPE: constants.DATETYPES.YEARDATETIME,
              canAcceptNull: true
            }
          },
          {
            headerName: "Org " + aptConfig.headerName,
            headerTooltip: "Original " + aptConfig.headerTooltip,
            field: "originalShipmentCTA",
            minWidth: constants.columnWidth.DELIVERY_WINDOW,
            cellClass: "gridCell-leftAlign",
            filterFramework: DateTimeFilter,
            filterParams: {
              DATETYPE: constants.DATETYPES.DATETIME,
              isTimeInterval: true,
            },
            valueFormatter: GridConfig.valueFormatterFFEAPT,
          },
        ];
      } else {
        return [
          {
            headerName: aptConfig.headerName,
            headerTooltip: aptConfig.headerTooltip,
            field: "shipmentCTA",
            minWidth: constants.columnWidth.EARLY_LATE_CTA + 50,
            cellClass: "gridCell-leftAlign",
            filterFramework: DateTimeFilter,
            filterParams: {
              DATETYPE: constants.DATETYPES.DATETIME,
              isTimeInterval: true,
              useGetterValue: true,
            },
          },
        ];
      }
    }

      static getDefaultConfiguration({
        featureFlags,
        terminalAccessLabel,
        ROUTENAME_EDITOR_PARAMS,
        columnConfig,
      }) {
        const aptConfig = GridConfig.getAPTWindowConfig(columnConfig);

        const defaultConfig = {
        title: constants.gridNames.ShipmentsGrid,
        keyColumn: "proId",
        rowSelection: "multiple",
        rowDeselection: "true",
        defaultColDef:{
          cellEditorFramework: TextFieldEditor,
          sortable: true,
          filter: true,
          resizable: true
        },
        columnTypes:{
          "isCancelBeforeStart":{ }
        },
        columns: [
          {
            headerName: "",
            minWidth: constants.columnWidth.VIOLATION_DOT,
            maxWidth: constants.columnWidth.VIOLATION_DOT,
            field:"violation",
            pinned: "left",
            lockPosition: true,
            cellClass: ["gridCell-centerAlign", "gridCell-dot"],
            filter: false,
            cellRendererFramework: DotRenderer,
            isInvisibleInShowHide: true,
            gridTitle: constants.gridNames.IBShipmentsGrid,
          },
            {
              headerName: "Route",
              headerTooltip: "Route",
              field: "routeName",
              editable:GridConfig.isRouteNameEditable,
              pinned: "left",
              lockPosition: true,
              minWidth: constants.columnWidth.ROUTES,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              cellEditorFramework: DropdownEditor,
              cellEditorParams: {
                canAcceptNewValue: true,
              ...ROUTENAME_EDITOR_PARAMS,
              },
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressEnter
            },
            {
              headerName: "Pro#",
              field: "proId",
              headerTooltip: "Pro# ID",
              pinned: "left",
              lockPosition: true,
              minWidth: constants.columnWidth.PROID,
              cellClass: GridConfig.getProNumberClasses,
              filterFramework: MultiSelectFilter,
              filterParams:{
                filterType: constants.FILTER_TYPES.STRING
              },
            },
            {
              headerName: "Original Pro#",
              field: "originalProId",
              minWidth: constants.columnWidth.PROID,
              hide: true,
              isInvisibleInShowHide: true,
              hideColumnInGridShowInCSV: true
            },
            {
              headerName: "Customer",
              headerTooltip: "Customer Name",
              field: "consigneeName",
              minWidth: constants.columnWidth.CUSTOMER,
              valueGetter : GridConfig.getCustomerName,
              pinned: "left",
              lockPosition: true,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value
            },
            {
              headerName: "Spl Needs",
              headerTooltip: "Special Needs",
              minWidth: constants.columnWidth.SPL_NEED,
              cellClass: "gridCell-leftAlign",
              valueGetter: SpecialNeedsValueGetter,
              filterFramework: StaticFilter,
              filterParams: {
                getStaticOptions: GridConfig.getSpecialNeedsOption,
              },
              cellRendererParams:{
                getAPTWindow: aptConfig.valueGetter,
              },
              cellRendererFramework: SpecialNeedsRenderer,
              pinned: "left",
            },
            {
              headerName: "Shipment note",
              headerTooltip: "Shipment note",
              field: "shipmentComments",
              minWidth: constants.columnWidth.W_150,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField: "shipmentComments"
            },
            {
              headerName: "Address",
              headerTooltip: "Customer Address",
              field: "consigneeAddress",
              minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              valueGetter : GridConfig.getCustomerAddress,
            },
            {
              headerName: "City",
              headerTooltip: "City",
              field: "city",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.CITY,
              filterFramework: MultiSelectFilter,
              tooltipValueGetter: (params) => params.value,
              valueGetter : GridConfig.getCustomerCity,
            },
            {
              headerName: "State",
              headerTooltip: "State",
              field: "state",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.STATE,
              filterFramework: MultiSelectFilter,
              valueGetter : GridConfig.getCustomerState,
            },
            {
              headerName: "Zip",
              headerTooltip: "Zip",
              field: "zip",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.ZIP_CODE,
              filterFramework: MultiSelectFilter,
              valueGetter : GridConfig.getCustomerZip,
            },
            {
              headerName: "HU",
              headerTooltip: "Handling Unit",
              field: "hu",
              minWidth: constants.columnWidth.HU,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              editable: false,
              //cellEditorFramework: NumericEditor,
            },
            {
              headerName: "Geocoding",
              headerTooltip: "Geocoding",
              valueGetter: GridConfig.geoCodeValueGetter,
              minWidth: constants.columnWidth.GEOCODING,
              cellClass: "gridCell-left,Align",
              editable: false,
              cellRendererFramework: GeoCodeRenderer,
              filterFramework: StaticFilter,
              filterParams: {
                staticOptions: Object.values(constants.geoCode),
                renderIcon: iconRenderer
              },
            },
            {
              headerName: "Package Type",
              headerTooltip: "Package Type",
              field: "packageType",
              cellClass: "gridCell-leftAlign",
              minWidth: constants.columnWidth.PACKAGE_TYPE,
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "Pallet Count",
              headerTooltip : "Pallet Count",
              field: "palletCount",
              minWidth: constants.columnWidth.PALLET_COUNT,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
            },
            {
              headerName: "Frozen Pallets",
              field: "freezerPlts",
              headerTooltip: "Frozen Pallets",
              minWidth: constants.columnWidth.FROZEN_PALLETS,
              cellRendererFramework:GridConfig.FrozenAndNonFrozenPalletRenderer,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"freezerPlts",
              editable: featureFlags.isFFEConfig,             
              cellEditorFramework: NumericEditor,
            },
            {
              headerName: "Non-Frozen Pallets",
              field: "coolerPlts",
              headerTooltip: "Non-Frozen Pallets",
              minWidth: constants.columnWidth.NON_FROZEN_PALLETS,
              cellRendererFramework:GridConfig.FrozenAndNonFrozenPalletRenderer,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"coolerPlts",
              editable: featureFlags.isFFEConfig,             
              cellEditorFramework: NumericEditor,
            },
            {
              headerName: "Temp Type",
              field: "tempType",
              headerTooltip: "Temp Type",
              minWidth: constants.columnWidth.TEMP_TYPE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"tempType"
            },
            {
              headerName: "Pieces",
              headerTooltip : "Pieces",
              field: "pieces",
              minWidth: constants.columnWidth.PIECES,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
            },
            {
              headerName: "PSE",
              field: "pse",
              minWidth: constants.columnWidth.PSE,
              editable: !featureFlags.isFFEConfig,
              headerTooltip : "Pallet Space Equivalent",
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              cellEditorFramework: NumericEditor,
              hide: featureFlags.IsWeightBasedRoutingEnabled
              //tooltipField: "pse"
            },
            {
              headerName: "Calculated PSE",
              field: "calculatedPse",
              minWidth: constants.columnWidth.CALCULATED_PSE,
              headerTooltip : "Calculated Pallet Space Equivalent",
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide: true
            }, 
            {
              headerName: "Weight",
              headerTooltip: "Weight",
              field: "weight",
              minWidth: constants.columnWidth.WEIGHT,
              cellClass: "gridCell-centerAlign",
              filterFramework: MultiSelectFilter,
              editable: true,
              cellEditorFramework: NumericEditor,
            },
            {
              headerName: "Shipment Status",
              headerTooltip: "Shipment Status",
              field: "shipmentStatus",
              minWidth: constants.columnWidth.SHIPMENT_STATUS,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField: "shipmentStatus"
            },
            {
              headerName: "Due Date",
              headerTooltip: "Due Date",
              field: "dueDate",
              minWidth: constants.columnWidth.DUE_DATE,
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              }
            },
            // {
            //   headerName: "Service",
            //   headerTooltip: "Service",
            //   field: "priority",
            //   minWidth: constants.columnWidth.PRIORITY,
            //   cellClass: "gridCell-leftAlign",
            //   filterFramework: MultiSelectFilter
            // },
           ...GridConfig.getAPTWindowColumnConfig(
            aptConfig,
            featureFlags
          ),
            {
              headerName: "Flexi Apt",
              headerTooltip: "Flexible Appointment",
              field: "isFlexiApt",
              editable: true,
              minWidth: constants.columnWidth.W_100,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              valueFormatter: GridConfig.getFlexibleAppointment,
              valueSetter: GridConfig.setFlexibleAppointment,
              filterValueGetter: GridConfig.getFlexibleAppointment,
              cellEditorFramework: DropdownEditor,
              suppressKeyboardEvent: suppressEnter,
              cellEditorParams: {
                isClearable: false,
              },
              hide:true
            },
            {
              headerName: "Delivery Window",
              headerTooltip: "Delivery Window",
              field: "deliveryWindow",
              minWidth: constants.columnWidth.DELIVERY_WINDOW + 50,
              cellClass: "gridCell-leftAlign",
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
                isTimeInterval: true,
                useGetterValue: true,
              },
              // editable: false,
              // valueFormatter: formatDeliveryWindow,
            },
            {
              headerName: "LG",
              headerTooltip: "Lift Gate",
              field: "liftgate",
              editable: true,
              minWidth: constants.columnWidth.LG,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              cellEditorFramework: DropdownEditor,
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressEnter
            },
            // {
            //   headerName: "Spl Needs",
            //   headerTooltip: "Special Needs",
            //   field: "specialNeeds",
            //   minWidth: constants.columnWidth.SPL_NEED,
            //   cellClass: "gridCell-leftAlign",
            //   filterFramework: MultiSelectFilter
            // },
            // {
            //   headerName: "Equip Pref",
            //   headerTooltip: "Equipment Preference",
            //   field: "eqpmtPref",
            //   minWidth: constants.columnWidth.EQUIPMENT_PREFERENCE,
            //   cellClass: "gridCell-leftAlign",
            //   filterFramework: MultiSelectFilter
            // },
            {
              headerName: "Area",
              headerTooltip: "Area",
              field: "zoneName",
              minWidth: constants.columnWidth.AREA,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "Curr Trlr",
              headerTooltip: "Trailer ID",
              field: "trailerNumber",
              minWidth: constants.columnWidth.TRAILER_ID,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "IB ETA",
              headerTooltip: "Shipment ETA",
              field: "shipmentEtaDateTime",
              minWidth: constants.columnWidth.IBETA,
              valueFormatter: formatDateTime,
              cellRendererFramework: IBETARenderer,
              editable: GridConfig.IBETAEditable,
              cellEditorFramework: DateTimeEditor,
              cellEditorParams:{
                DATETYPE: constants.DATETYPES.DATETIME
              },
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME
              }
            },
            {
              headerName: "Apt Due Date",
              headerTooltip: "Apt Due Date",
              field: "aptDueDate",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              }
            },
            {
              headerName: "Is APT Edited",
              headerTooltip: "Appointment Window Edited",
              minWidth: constants.columnWidth.W_160,
              field: "isAptWindowModified",
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide: true,
              valueGetter: GridConfig.aptWindowModifiedValueGetter,
            },
            {
            headerName: `Orig ${terminalAccessLabel.suffix}`,
            headerTooltip: `Origin ${terminalAccessLabel.fullName}`,
              field: "originTerminal",
              minWidth: constants.columnWidth.TERMINAL,
            filterFramework: MultiSelectFilter,
            },
            {
            headerName: `Curr ${terminalAccessLabel.suffix}`,
            headerTooltip: `Current ${terminalAccessLabel.fullName}`,
              field: "currentTerminal",
              minWidth: constants.columnWidth.TERMINAL,
            filterFramework: MultiSelectFilter,
            },
            {
            headerName: `Next ${terminalAccessLabel.suffix}`,
            headerTooltip: `Next ${terminalAccessLabel.fullName}`,
              field: "nextTerminal",
              minWidth: constants.columnWidth.TERMINAL,
              filterFramework:MultiSelectFilter
            },
            // {
            //   headerName: "Wave",
            //   headerTooltip: "Wave",
            //   field: "wave",
            //   minWidth: constants.columnWidth.WAVE,
            //   cellClass: "gridCell-leftAlign",
            //   filterFramework: MultiSelectFilter
            // },
            {
              headerName: "Curr Mfst",
              headerTooltip: "Manifest ID",
              field: "manifestNumber",
              minWidth: constants.columnWidth.MANIFEST_ID,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "Return Reason",
              headerTooltip: "Return Reason",
              field: "returnCode",
              minWidth: constants.columnWidth.RETURN_REASON,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              //tooltipField: "returnCode"

            },
            {
              headerName: "ILINE",
              headerTooltip: "ILINE",
              field: "iLine",
              minWidth: constants.columnWidth.ILINE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "Order#",
              headerTooltip: "Order Number",
              field: "orderNumber",
              minWidth: constants.columnWidth.ORDER_NUMBER,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter
            },
            {
              headerName: "Cubic Feet",
              headerTooltip: "Cubic Feet",
              field: "cube",
              minWidth: constants.columnWidth.CUBIC_FEET,
              cellClass: "gridCell-centerAlign",
              filterFramework: MultiSelectFilter,
            },
            {
              headerName: "Prep time",
              headerTooltip: "Preparation Time",
              field: "preparationTime",
              minWidth: constants.columnWidth.PREP_TIME,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              editable: true,
              cellEditorFramework: NumericEditor,
            },
            {
              headerName: "Customer#",
              headerTooltip: "Customer Account Number",
              minWidth: constants.columnWidth.CUSTOMER_CODE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide: true,
              valueGetter : GridConfig.getCustomerCode,
              tooltipValueGetter: (params) => params.value,
            },
            {
              headerName: "Latitude",
              field: "latitude",
              minWidth: constants.columnWidth.LATITUDE,
              //editable: GridConfig.isEditable,
              headerTooltip : "Latitude",
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField: "latitude",
              hide: true
            },
            {
              headerName: "Longitude",
              field: "longitude",
              minWidth: constants.columnWidth.LONGITUDE,
              //editable: GridConfig.isEditable,
              headerTooltip : "Longitude",
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField: "longitude",
              hide: true
            },
            {
              headerName: "Act Arr Time",
              headerTooltip: "Actual Arrival Time",
              field: "actualArrivalTime",
              minWidth: constants.columnWidth.IBETA,
              cellClass: "gridCell-leftAlign",
              valueFormatter: formatDateTime,
              hide: true,
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              }
            },
            {
              headerName: "Stop Seq",
              field: "stopSequence",
              headerTooltip: "Stop Sequence",
              minWidth: constants.columnWidth.STOP_SEQUENCE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide:true
            },
            {
              headerName: "Ldg Zone",
              field: "loadingZone",
              headerTooltip: "Loading Zone",
              minWidth: constants.columnWidth.LDG_ZONE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide: true,
            },
            {
              headerName: "Del Type",
              field: "rwwhen",
              headerTooltip: "Del Type",
              minWidth: constants.columnWidth.SHIPMENT_STATUS,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
            },
            {
              headerName: "REQ From",
              field: "mabdFrom",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              //editable: GridConfig.isEditable,
              headerTooltip : "REQ From",
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              },
              hide: columnConfig !== COLUMN_CONFIG.SAIA,
            },
            {
              headerName: "REQ/MABD To",
              field: "mabdTo",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              //editable: GridConfig.isEditable,
              headerTooltip : "REQ/MABD To",
              cellClass: "gridCell-leftAlign",
              valueFormatter:formatDate,
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATE,
              },
              hide: columnConfig !== COLUMN_CONFIG.SAIA,
            },
            {
              headerName: "Shipper",
              field: "shipperName",
              headerTooltip: "Shipper Name",
              minWidth: constants.columnWidth.CUSTOMER,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperName"
            },
            {
              headerName: "Shipper #",
              field: "shipperCode",
              headerTooltip: "Shipper Code",
              minWidth: constants.columnWidth.CUSTOMER_CODE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperCode"
            },
            {
              headerName: "Shipper Address",
              field: "shipperAddress",
              headerTooltip: "Shipper Address",
              minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperAddress"
            },
            {
              headerName: "Shipper City",
              field: "shipperCity",
              headerTooltip: "Shipper City",
              minWidth: constants.columnWidth.CITY,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperCity"
            },
            {
              headerName: "Shipper State",
              field: "shipperState",
              headerTooltip: "Shipper State",
              minWidth: constants.columnWidth.SHIPPER_STATE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperState"
            },
            {
              headerName: "Shipper Zip",
              field: "shipperZip",
              headerTooltip: "Shipper Zip",
              minWidth: constants.columnWidth.SHIPPER_STATE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperZip"
            },
            {
              headerName: "Spl Instructions",
              field: "shipmentSpecialInstruction",
              headerTooltip: "Special Instructions",
              minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipmentSpecialInstruction",
              hide: false
            },
            {
              headerName: "Max Trailer",
              headerTooltip: "Max Feasible Trailer Length",
              field: "maxFeasibleTrailerLength",
              editable:true,
              minWidth: constants.columnWidth.MAX_TRAILER,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              cellEditorFramework: DropdownEditor,
              cellEditorParams: {
                editorFieldName:"trailerLength",
              },
              cellRenderer: DropdownValueCellRenderer,
              suppressKeyboardEvent: suppressEnter
            },
            {
              headerName: "Shipment Type",
              headerTooltip : "Shipment Type",
              field: "isDelivery",
              minWidth: constants.columnWidth.STOP_TYPE,
              valueGetter: GridConfig.shipmentTypeValueGetter,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              hide: true
            },
            {
              headerName: "Origin Area",
              field: "originArea",
              headerTooltip: "Origin Area",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"originArea"
            },
            {
              headerName: "Destination Area",
              field: "destinationArea",
              headerTooltip: "Destination Area",
              minWidth: constants.columnWidth.W_150,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"destinationArea"
            },
            {
              headerName: "Last Updated",
              headerTooltip: "Last Updated",
              field: "lastUpdated",
              minWidth: constants.columnWidth.IBETA,
              cellClass: "gridCell-leftAlign",
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              }
            },
            {
              headerName: "Pickup Date",
              field: "pickupDate",
              headerTooltip: "Pickup Date",
              minWidth: constants.columnWidth.APT_DUE_DATE,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"pickupDate"
            },
            ...(featureFlags.isFFEConfig ? [{
              headerName: "Service DateTime",
              field : "deliveryDateTime", 
              valueGetter : (params) => getFormattedDeliveryDateTime(params.data.deliveryDateTime),
              headerTooltip: "Service DateTime",
              minWidth: constants.columnWidth.W_160,
              cellClass: "gridCell-leftAlign",
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              },
            }]: []),
            {
              headerName: "Group Id",
              field: "groupId",
              headerTooltip: "Group Id",
              minWidth: constants.columnWidth.W_100,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              editable: featureFlags.isFFEConfig,             
              cellEditorFramework: NumericEditor,
            },
            {
              headerName: "At Dest Term Flag",
              field: "isShipmentAtDestTerminal ",
              headerTooltip: "At Dest Term Flag",
              minWidth: constants.columnWidth.W_160,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"isShipmentAtDestTerminal",
              valueGetter: GridConfig.shipmentAtDestTerminalValueGetter,
            },
            {
              headerName: "INB Trailer",
              field: "inbTrailer",
              headerTooltip: "INB Trailer",
              minWidth: constants.columnWidth.W_120,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"inbTrailer"
            },
            {
              headerName: "INB Manifest",
              field: "inbManifest",
              headerTooltip: "INB Manifest",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"inbManifest"
            },
            {
              headerName: "Current Unit",
              field: "currentUnit",
              headerTooltip: "Current Unit",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"currentUnit"
            },
            {
              headerName: "Current Driver Code",
              field: "currentDriverCode",
              headerTooltip: "Current Driver Code",
              minWidth: constants.columnWidth.W_170,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"currentDriverCode"
            },
            {
              headerName: "Current Driver Name",
              field: "currentDriverName",
              headerTooltip: "Current Driver Name",
              minWidth: constants.columnWidth.W_170,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"currentDriverName"
            },
            {
              headerName: "Current Manifest Route",
              field: "currentManifestRoute",
              headerTooltip: "Current Manifest Route",
              minWidth: constants.columnWidth.W_180,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"currentManifestRoute"
            },
            {
              headerName: "Bill of Lading Number",
              field: "bolNumber",
              headerTooltip: "Bill of Lading Number",
              minWidth: constants.columnWidth.W_170,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"bolNumber"
            },
            {
              headerName: "Shipper Ref Number",
              field: "shipperReferenceNumber",
              headerTooltip: "Shipper Ref Number",
              minWidth: constants.columnWidth.W_170,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"shipperReferenceNumber"
            },
            {
              headerName: "Purchase Order Number",
              field: "poNumber",
              headerTooltip: "Purchase Order Number",
              minWidth: constants.columnWidth.W_190,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"poNumber"
            },
            {
              headerName: "Pickup Number",
              field: "pickupNumber",
              headerTooltip: "Pickup Number",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"pickupNumber"
            },
            {
              headerName: "Del Confirmation Number",
              field: "delConfirmationNumber",
              headerTooltip: "Del Confirmation Number",
              minWidth: constants.columnWidth.W_200,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"delConfirmationNumber"
            },
            {
              headerName: "Order Entry Datetime",
              field: "orderEntryDateTime",
              headerTooltip: "Order Entry Datetime",
              minWidth: constants.columnWidth.W_170,
              cellClass: "gridCell-leftAlign",
              tooltipField:"orderEntryDateTime",
              filterFramework: DateTimeFilter,
              filterParams: {
                DATETYPE: constants.DATETYPES.DATETIME,
              }
            },
            {
              headerName: "LoadAt Phone#",
              field: "loadAtPhoneNumber",
              headerTooltip: "LoadAt Phone#",
              minWidth: constants.columnWidth.W_140,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"loadAtPhoneNumber"
            },
            {
              headerName: "Consignee Phone#",
              field: "consigneePhoneNumber",
              headerTooltip: "Consignee Phone#",
              minWidth: constants.columnWidth.W_160,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"consigneePhoneNumber"
            },
            {
              headerName: "Nbr of Del appts",
              field: "numDelAppts",
              headerTooltip: "Nbr of Del appts",
              minWidth: constants.columnWidth.W_150,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"numDelAppts"
            },
            {
              headerName: "Bill To Code",
              field: "billToCode",
              headerTooltip: "Bill To Code",
              minWidth: constants.columnWidth.W_160,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"billToCode"
            },
            {
              headerName: "Bill To Name",
              field: "billToName",
              headerTooltip: "Bill To Name",
              minWidth: constants.columnWidth.W_160,
              cellClass: "gridCell-leftAlign",
              filterFramework: MultiSelectFilter,
              tooltipField:"billToName"
            }
          ]
      }
      if (columnConfig === COLUMN_CONFIG.SAIA) {
        defaultConfig.columns = defaultConfig.columns.filter(
          (column) => column.field !== "deliveryWindow"
        );
      }
      return defaultConfig;
    }
}